import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { IconButton } from '@material-ui/core';

import { RiFilter3Line } from '@remixicon/react';

import { useFilterShortcut } from '../../hooks';
import { SecondaryIconButtonMui as SecondaryIconButton } from '../SecondaryIconButtonMui';
import { FilterEditorPopoverMui } from './FilterEditorPopoverMui';
import { FilterSelectMui } from './FilterSelectMui';
import { FILTER_CONTEXT, type FilterDefinition } from './provider';

interface AddFilterButtonMuiProps {
  filters?: FilterDefinition[]; // Override the filter list shown in the menu
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'small' | 'medium';
}

export const AddFilterButtonMui = ({
  filters: filtersOverride,
  variant = 'secondary',
  size = 'small',
}: AddFilterButtonMuiProps) => {
  const context = useContext(FILTER_CONTEXT);

  if (!context)
    throw new Error('AddFilterButton must be used within a FiltersProvider');

  const { addFilter, activeFilters } = context;
  const filters = filtersOverride ?? context.filters;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [pendingFilter, setPendingFilter] = useState<FilterDefinition | null>(
    null,
  );
  const [pendingValue, setPendingValue] = useState<unknown>(null);
  const [filterShortcutEnabled] = useFilterShortcut();

  const canAddFilter = filters.length !== 0;
  const hasActiveFilters = activeFilters.length !== 0;
  const showButton = canAddFilter || hasActiveFilters;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        event.key !== 'f' ||
        !event.metaKey ||
        !event.shiftKey ||
        !filterShortcutEnabled
      ) {
        return;
      }
      event.preventDefault();
      buttonRef.current?.focus();
      setAnchorEl(buttonRef.current);
    },
    [filterShortcutEnabled],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const closeMenu = () => {
    setPendingFilter(null);
    setPendingValue(null);
    setAnchorEl(null);
  };

  if (!showButton) return null;

  const ButtonComponent =
    variant === 'secondary' ? SecondaryIconButton : IconButton;

  return (
    <>
      <ButtonComponent
        ref={buttonRef}
        onClick={e => setAnchorEl(e.currentTarget)}
        disabled={!canAddFilter}
        size={size}
        aria-label="Add filter"
       data-track-source-anchor="ba70299571">
        <RiFilter3Line size={16} />
      </ButtonComponent>
      {anchorEl && !pendingFilter && (
        <FilterSelectMui
          filters={filters}
          anchorEl={anchorEl}
          onClose={closeMenu}
          onSelect={filter => {
            setPendingFilter(filter);
            setPendingValue(null);
          }}
        />
      )}
      {anchorEl && pendingFilter && (
        <FilterEditorPopoverMui anchorEl={anchorEl} onClose={closeMenu}>
          {pendingFilter.renderEditor({
            value: pendingValue,
            onValueChange: value => {
              setPendingValue(value);
              addFilter(pendingFilter, value);
            },
            onClose: closeMenu,
          })}
        </FilterEditorPopoverMui>
      )}
    </>
  );
};
